import { z } from 'zod';

import { getToolApprovalForToolCall, getToolOutputForToolCall } from './chat/ai-sdk-ui-messages';
import type { ToolCall } from './chat/tool-calls';

const CREATED_REPORT_SCHEMA = z.object({
  reportId: z.string().uuid(),
  status: z.literal('created'),
});

export interface ReportCreationPresentation {
  description: string;
  processing: boolean;
  title: string;
}

/** Present the saved outcome first, including when the markdown card is stale after reload. */
export function getReportCreationPresentation({
  running,
  toolCall,
  uiParts,
}: {
  toolCall: Extract<ToolCall, { type: 'createReport' }>;
  /** Omitted by serializers without message state; only false confirms the run ended. */
  running?: boolean;
  uiParts?: unknown[] | null;
}): ReportCreationPresentation {
  const approval = getToolApprovalForToolCall(uiParts, toolCall.id);
  const output = getToolOutputForToolCall(uiParts, toolCall.id);
  const description = toolCall.name ? `Report: ${toolCall.name}` : '';
  if (output) {
    const created = CREATED_REPORT_SCHEMA.safeParse(parseOutput(output.output));
    if (output.state === 'output-available' && created.success)
      return { description, processing: false, title: 'Created report' };
    return {
      description: 'The report could not be created. Please try again.',
      processing: false,
      title: 'Could not create report',
    };
  }
  if (toolCall.reportId) return { description, processing: false, title: 'Created report' };
  if (approval?.approved === false)
    return {
      description,
      processing: false,
      title: approval.reason ? 'Report plan revised' : 'Report plan skipped',
    };
  if (approval?.approved === true)
    return {
      description,
      processing: running === true,
      title: running === false ? 'Report creation interrupted' : 'Creating report...',
    };
  if (running === false && !approval)
    return { description, processing: false, title: 'Report creation interrupted' };
  if (toolCall.preparing)
    return {
      description: 'Working out the columns and filters for your review.',
      processing: running === true,
      title: 'Preparing report plan...',
    };
  return { description, processing: running === true, title: 'Waiting on plan approval' };
}

function parseOutput(output: unknown): unknown {
  if (typeof output !== 'string') return output;
  try {
    return JSON.parse(output);
  } catch {
    return undefined;
  }
}
